import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import SuperLayout from '../layout/SuperAdminLayout';
import { useAuth } from '../../context/AuthContext';
import { Statistics, AdminReport } from "../../services/api";

ChartJS.register(ArcElement, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Title);

const SuperAdminRepo = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user.role !== 'SUPER_ADMIN') {
    return (
      <div className="flex items-center justify-center min-h-screen text-xl text-red-600">
        Access Denied
      </div>
    );
  }

  const { data: stats = {}, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['adminStatistics'],
    queryFn: Statistics,
  });

  const { data: report = {}, isLoading: reportLoading, error: reportError } = useQuery({
    queryKey: ['adminReport'],
    queryFn: AdminReport,
  });

  const totalRevenue = report.sum_total_amount || report.sum_amount || stats.total_revenue || 0;
  const totalInvoices = report.total_invoices || 0;
  const totalTransactions = report.total_invoices || stats.total_transactions || 0;

  const paymentMethodData = useMemo(() => {
    const methods = report.method_counts || {};
    const labels = [];
    const values = [];
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

    Object.entries(methods).forEach(([method, info], idx) => {
      if (info.count > 0) {
        labels.push(method.charAt(0).toUpperCase() + method.slice(1));
        values.push(info.count);
      }
    });

    return {
      labels,
      datasets: [{
        data: values,
        backgroundColor: colors.slice(0, labels.length),
        borderWidth: 0,
      }],
    };
  }, [report]);

  const statusBreakdown = useMemo(() => {
    const counts = report.status_counts || {};
    return {
      pending: counts.pending?.count || 0,
      completed: counts.completed?.count || 0,
      failed: counts.failed?.count || 0,
      refunded: counts.refunded?.count || 0,
      pendingAmount: counts.pending?.sum_amount || 0,
      completedAmount: counts.completed?.sum_amount || 0,
      failedAmount: counts.failed?.sum_amount || 0,
      refundedAmount: counts.refunded?.sum_amount || 0,
    };
  }, [report]);

  const methodBreakdown = useMemo(() => {
    const methods = report.method_counts || {};
    return {
      walletCount: methods.wallet?.count || 0,
      walletAmount: methods.wallet?.sum_amount || 0,
      flutterwaveCount: methods.flutterwave?.count || 0,
      monnifyCount: methods.monnify?.count || 0,
      remitaCount: methods.remita?.count || 0,
      bankTransferCount: methods.bank_transfer?.count || 0,
    };
  }, [report]);

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { enabled: true },
    },
  };

  const isLoading = statsLoading || reportLoading;
  const hasError = statsError || reportError;

  return (
    <SuperLayout>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Overview</h2>
        <p className="text-gray-600 mt-1">Real-time insight into platform performance</p>
      </div>

      {hasError ? (
        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg">
          Error loading dashboard data. Please try again later.
        </div>
      ) : isLoading ? (
        <div className="text-center py-20 text-gray-500 text-lg">
          Loading dashboard...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-6 mb-10">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Total Revenue</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                ₦{Number(totalRevenue).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Total Invoices</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {totalInvoices.toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Completed</p>
              <p className="text-3xl font-bold text-green-700 mt-2">
                {statusBreakdown.completed}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                ₦{Number(statusBreakdown.completedAmount).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Failed</p>
              <p className="text-3xl font-bold text-red-700 mt-2">
                {statusBreakdown.failed}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                ₦{Number(statusBreakdown.failedAmount).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Pending</p>
              <p className="text-3xl font-bold text-yellow-700 mt-2">
                {statusBreakdown.pending}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                ₦{Number(statusBreakdown.pendingAmount).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Wallet Payments</p>
              <p className="text-3xl font-bold text-blue-700 mt-2">
                {methodBreakdown.walletCount}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                ₦{Number(methodBreakdown.walletAmount).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-5">Payment Method Distribution</h3>
              <div className="h-72">
                <Doughnut data={paymentMethodData} options={chartOptions} />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-5">Monthly Invoice Status Trend</h3>
              <div className="h-72">
                <Line data={{
                  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                  datasets: [
                    {
                      label: 'Completed',
                      data: new Array(12).fill(0),
                      borderColor: '#10B981',
                      backgroundColor: '#10B98133',
                      tension: 0.4,
                      fill: true,
                    },
                    {
                      label: 'Failed',
                      data: new Array(12).fill(0),
                      borderColor: '#EF4444',
                      backgroundColor: '#EF444433',
                      tension: 0.4,
                      fill: true,
                    },
                    {
                      label: 'Pending',
                      data: new Array(12).fill(0),
                      borderColor: '#F59E0B',
                      backgroundColor: '#F59E0B33',
                      tension: 0.4,
                      fill: true,
                    },
                  ],
                }} options={chartOptions} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Monnify Payments</p>
              <p className="text-2xl font-bold text-purple-700 mt-2">
                {report.method_counts?.monnify?.count || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                ₦{(report.method_counts?.monnify?.sum_amount || 0).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Remita Payments</p>
              <p className="text-2xl font-bold text-indigo-700 mt-2">
                {report.method_counts?.remita?.count || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                ₦{(report.method_counts?.remita?.sum_amount || 0).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Flutterwave Payments</p>
              <p className="text-2xl font-bold text-blue-700 mt-2">
                {report.method_counts?.flutterwave?.count || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                ₦{(report.method_counts?.flutterwave?.sum_amount || 0).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Bank Transfer</p>
              <p className="text-2xl font-bold text-gray-800 mt-2">
                {report.method_counts?.bank_transfer?.count || 0}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                ₦{(report.method_counts?.bank_transfer?.sum_amount || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </>
      )}
    </SuperLayout>
  );
};

export default SuperAdminRepo;